// src/features/energyxplorer/hooks/useEnergyTreeNode.js
// react
import { useCallback } from 'react';

// tanstack
import { useParams } from '@tanstack/react-router';

// api
// import { useGetEnergyTreeNodeByIdQuery } from '@/api/recalcV2/endpoints/energyTreeNodesApiSlice';
import { useGetEnergyGroupEnergyTreeNodeQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// components
import useSearchParams from '@/hooks/useStableSearchParams';
// 👆 Adjust path if your slice lives elsewhere

const NODE_PARAM_KEY = 'node';

/**
 * A unified hook for reading, setting, and clearing the currently
 * selected energyTreeNodeId in the URL, and fetching the node via RTK Query.
 */
export function useEnergyTreeNode(options = {}) {
  const {
    paramKey = NODE_PARAM_KEY,
    accountIdOverride = null,
    extraSkip = false,
  } = options;

  const { accountId: accountIdFromRoute } = useParams({ strict: false });
  const { energyGroupId: energyGroupIdFromRoute } = useParams({
    strict: false,
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const accountId = accountIdOverride || accountIdFromRoute || null;
  const nodeId = searchParams.get(paramKey) || null;

  const setNodeId = useCallback(
    (id) => {
      setSearchParams((prev) => {
        let next = new URLSearchParams(prev);
        if (!id) {
          next.delete(paramKey);
        } else {
          next.set(paramKey, id);
        }
        return next;
      });
    },
    [setSearchParams, paramKey],
  );

  const clearNodeId = useCallback(
    (opts = {}) => setNodeId(null, opts),
    [setNodeId],
  );

  const shouldSkip = extraSkip || !accountId || !nodeId;

  const {
    data: energyTreeNode,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetEnergyGroupEnergyTreeNodeQuery(
    { groupId: energyGroupIdFromRoute, nodeId: nodeId },
    { skip: shouldSkip },
  );

  return {
    // URL + routing state
    accountId,
    nodeId,

    // RTK Query results
    energyTreeNode,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,

    // URL control
    setNodeId,
    clearNodeId,
  };
}
